import { usePage } from '@inertiajs/react';
import { ReactNode } from 'react';
import { ChevronRight } from 'react-feather';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { setServerErrors, useMutation } from '../../Shared/api';
import { Button } from '../../Shared/UI/Button';
import { Card } from '../../Shared/UI/Card';
import ErrorMessage from '../../Shared/UI/ErrorMessage';
import { Icon } from '../../Shared/UI/Icon';
import { Input } from '../../Shared/UI/Input';
import InputGroup from '../../Shared/UI/InputGroup';
import InputLabel from '../../Shared/UI/InputLabel';
import ServerErrors from '../../Shared/UI/ServerErrors';
import { useRouter } from '../router';
import { PageProps } from '../types';
import Container from '../UI/Container';
import Layout from '../UI/Layout';
import RegistrationProgress from '../UI/RegistrationProgress';

export default function RegisterPage() {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const { email } = usePage<PageProps<{ email?: string; }>>().props;

  const { register, handleSubmit, setError, formState: { errors, defaultValues } } = useForm({
    defaultValues: {
      email,
      first_name: '',
      last_name: '',
    },
  });

  const [signup, { loading, errors: serverErrors }] = useMutation(routes.register(), {
    onError: (errors) => {
      setServerErrors(errors, setError, defaultValues);
    },
  });

  return (
    <>
      <RegistrationProgress step="create_account" />
      <Container size="sm">
        <Card>
          <form onSubmit={handleSubmit(signup)}>
            <div className="space-y-6">
              <div>
                <h2 className="mb-1">
                  {t('backend:sign_up')}
                </h2>
                <p className="text-slate-700">
                  {t('backend:sign_up_intro')}
                </p>
              </div>

              {serverErrors && <ServerErrors errors={serverErrors} defaultValues={defaultValues} scrollIntoView />}

              <InputGroup>
                <InputLabel required valid={!errors.email} htmlFor="register_email">
                  {t('shared:form.email_address')}
                </InputLabel>
                <Input
                  {...register('email', { required: true })}
                  inputMode="email"
                  id="register_email"
                  aria-invalid={!!errors.email}
                />
                <ErrorMessage attribute={t('shared:form.email_address')} error={errors.email }/>
              </InputGroup>

              <InputGroup>
                <InputLabel required valid={!errors.first_name} htmlFor="register_first_name">
                  {t('shared:form.first_name')}
                </InputLabel>
                <Input
                  {...register('first_name', { required: true })}
                  id="register_first_name"
                  aria-invalid={!!errors.first_name}
                />
                <ErrorMessage attribute={t('shared:form.first_name')} error={errors.first_name }/>
              </InputGroup>

              <InputGroup>
                <InputLabel required valid={!errors.last_name} htmlFor="register_last_name">
                  {t('shared:form.last_name')}
                </InputLabel>
                <Input
                  {...register('last_name', { required: true })}
                  id="register_last_name"
                  aria-invalid={!!errors.last_name}
                />
                <ErrorMessage attribute={t('shared:form.last_name')} error={errors.last_name }/>
              </InputGroup>

              <Button
                type="submit"
                disabled={loading}
                className="block w-full"
                loading={loading}
              >
                {t('shared:form.continue')}
                <Icon className="ml-1">
                  <ChevronRight />
                </Icon>
              </Button>
            </div>
          </form>
        </Card>
        <div className="mt-8 text-center">
          <a href={routes.login_page()}>
            {t('backend:log_in')}
          </a>
        </div>
      </Container>
    </>
  );
}

RegisterPage.layout = (page: ReactNode) => <Layout>{page}</Layout>;
